import React, { useState, useEffect } from 'react';
import useTheme from '../ColorConfig/usetheme';

// Since we can't import react-icons, we'll create simple SVG icons
const WhatsAppIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.891 3.426"/>
  </svg>
);

const ArrowUpIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
  </svg>
);

const FloatingButtons = () => {
  const colors = useTheme();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Predefined message for WhatsApp
  const whatsappMessage = "Hello! I'm interested in your services. Could you please provide more information?";

  // Check if user has scrolled
  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Show arrow when scrolled down more than 300px
    if (scrollTop > 300) {
      setShowScrollTop(true);
    } else {
      setShowScrollTop(false);
    }
  };

  // Check if device is mobile
  const checkIsMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  return (
    <>
      <style>{`
        /* CSS Variables for consistent theming */
        // :root {
        //   --color-primary: #007bff;
        //   --color-white: #ffffff;
        // }

        /* Floating Buttons Container */
        .floating-buttons {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          gap: 0; /* Remove gap, we'll handle spacing with transforms */
          align-items: center;
        }

        /* WhatsApp Button - Always at bottom */
        .whatsapp-float {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 50px;
          height: 50px;
          background-color: #25D366;
          color: white;
          border-radius: 50%;
          text-decoration: none;
          box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateY(0);
          order: 2; /* Always stay at bottom */
        }

        .whatsapp-float:hover {
          background-color: #25D366;
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 6px 16px rgba(37, 211, 102, 0.5);
        }

        /* Scroll Top Button Container */
        .scroll-top-container {
          order: 1; /* Always above WhatsApp */
          margin-bottom: 15px; /* Space between buttons */
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Scroll Top Button */
        .scroll-top-float {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 50px;
          height: 50px;
          background-color: var(--color-primary);
          color: var(--color-white);
          border: none;
          border-radius: 50%;
          cursor: pointer;
        //   box-shadow: 0 4px 4px var(--color-primary);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Hidden state for scroll top button */
        .scroll-top-container.hidden {
          transform: translateY(80px) scale(0.3);
          opacity: 0;
          pointer-events: none;
          margin-bottom: -65px; /* Collapse space when hidden */
        }

        /* Visible state for scroll top button */
        .scroll-top-container.visible {
          transform: translateY(0) scale(1);
          opacity: 1;
          pointer-events: auto;
          margin-bottom: 15px; /* Normal space when visible */
        }

        /* Pulse animation for scroll top button */
        .scroll-top-container.visible .scroll-top-float {
          animation: gentle-pulse 2s infinite;
        }

        // @keyframes gentle-pulse {
        //   0%, 100% {
        //     box-shadow: 0 4px 12px var(--color-primary);
        //   }
        //   50% {
        //     box-shadow: 0 6px 16px var(--color-primary);
        //   }
        // }

        .scroll-top-float:hover {
          background-color: var(--color-white);
          color: var(--color-primary);
          transform: scale(1.1);
          box-shadow: 0 6px 20px var(--color-primary);
          animation: none; /* Remove pulse on hover */
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .floating-buttons {
            bottom: 20px;
            right: 15px;
          }
          
          .whatsapp-float {
            width: 45px;
            height: 45px;
          }
          
          .whatsapp-float svg {
            width: 22px;
            height: 22px;
          }
          
          .scroll-top-float {
            width: 45px;
            height: 45px;
          }
          
          .scroll-top-float svg {
            width: 22px;
            height: 22px;
          }
          
          .scroll-top-container {
            margin-bottom: 12px;
          }
          
          .scroll-top-container.visible {
            margin-bottom: 12px;
          }
          
          .scroll-top-container.hidden {
            margin-bottom: -58px;
          }
        }

        @media (max-width: 480px) {
          .floating-buttons {
            bottom: 15px;
            right: 10px;
          }
          
          .whatsapp-float {
            width: 40px;
            height: 40px;
          }
          
          .whatsapp-float svg {
            width: 20px;
            height: 20px;
          }
          
          .scroll-top-float {
            width: 40px;
            height: 40px;
          }
          
          .scroll-top-float svg {
            width: 20px;
            height: 20px;
          }
        }

        /* Additional smooth entrance animation */
        .floating-buttons {
          animation: slideIn 0.5s ease-out;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Ensure smooth scrolling for the page */
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      <div className="floating-buttons">
        {/* Scroll to Top Button - appears above WhatsApp when visible */}
        <div className={`scroll-top-container ${showScrollTop ? 'visible' : 'hidden'}`}>
          <button 
            className="scroll-top-float"
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <ArrowUpIcon />
          </button>
        </div>

        {/* WhatsApp Button - always at bottom */}
        <a 
          href={`https://wa.me/919840841840?text=${encodeURIComponent(whatsappMessage)}`}
          className="whatsapp-float"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contact us on WhatsApp"
        >
          <WhatsAppIcon />
        </a>
      </div>
    </>
  );
};

export default FloatingButtons;